/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnChanges, type OnInit, type SimpleChanges, Component, EventEmitter, Injector, Input, Output, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import {
    type Rest, ActionAbstract, FormControl, FormGroup, SelectComponent, TimeService, UNPLACEHOLDER, UtilService,
} from '@dm/core';
import { type CIFSClient, type CIFSShare, type FileSystem } from 'app/common/types/mo';
import { isEqual } from 'lodash';
import { type Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { FileSystemEntryE } from '../../../file-system/create-file-system/create-file-system.component';
import { CreateStyleE } from '../../share.enum';
import { CifsShareService } from '../cifs-share.service';
import { CifsShareStrService, OffLineFileModeE } from '../cifs-share-str.service';
import { ClientCifsShareService } from '../client-cifs-share/client-cifs-share.service';
import { CIFSUserPermissionsE, DomainTypeE, UserTypeE } from '../client-cifs-share/client-clifs-share';
import { CreateCifsShareComponent } from '../create-cifs-share/create-cifs-share.component';

export interface CreateShare {
    all: { share: CIFSShare; permissions: CIFSClient[] };
    new: { share: CIFSShare; permissions: CIFSClient[] };
    copyFrom: { share: CIFSShare; permissions: CIFSClient[]; permissionNumber: number };
}

@Component({
    selector: 'app-copy-from-cifs-share',
    templateUrl: './copy-from-cifs-share.component.html',
    styleUrls: ['./copy-from-cifs-share.component.scss'],
})

export class CopyFromCifsShareComponent extends ActionAbstract implements OnInit, OnChanges {
    @ViewChild('CIFSshareSelect') shareSelect: SelectComponent;
    @Input() vstoreId: string;

    @Input() vstoreName: string;

    /**
     * 文件系统名称
     */
    @Input() fsName: string;

    // 外部传入的vm
    @Input() vm: FormGroup;
    // 开关的状态
    @Input() shareSwitch = true;

    // 智能GUI推荐的共享参数
    @Input() smartGuiCifs?: CIFSShare;

    @Input() entry: FileSystemEntryE;

    /**
     * 是否支持智能GUI，默认支持
     */
    @Input() supportSmartGui = true;

    @Input() fileSystem: FileSystem = {};

    @Output() sendShareNameChangedData = new EventEmitter();

    i18n = this.str.i18n;
    UNPLACEHOLDER = UNPLACEHOLDER;
    shareRest: Rest;
    errorMessage: any;

    CreateStyleE = CreateStyleE;
    OffLineFileModeE = OffLineFileModeE;

    // 开关状态, 默认true
    switch = true;
    // 创建模式，默认new
    createStyle = CreateStyleE.New;
    // 选中的文件系统，默认null
    selectShareOpt = null;
    // 时间错，加载文件的时候就初始化好，避免重复计算
    private timestamp = this.timeService.formatUTC(this.timeService.currTime * 1e3, 'YYYYMMDDHHmmss', false).split(' ')[0];
    /**
     * 是否属于审计日志文件系统、哈希文件系统或内部文件系统
     */
    isAuditLogFs = false;


    // 查询共享权限的rest subscribe，避免快速点击的时候重复下发返回时序问题导致显示错误。
    $getCount: null | Subscription = null;

    /**
     * new模式下的默认权限
     */
    defaultNewPermission: CIFSClient = {
        ID: 'Everyone-0',
        NAME: '@Everyone',
        DOMAINTYPE: DomainTypeE.Local,
        PERMISSION: CIFSUserPermissionsE.All,
        userType: UserTypeE.Everyone,
    };

    /**
     * 待创建的共享数据，new和copyFrom分开，是为了记录数据。切换new和copyfrom的时候，数据不丢失。
     */
    createShare: CreateShare = {
        all: {
            share: {
                NAME: '',
                SHAREPATH: UNPLACEHOLDER,
                DESCRIPTION: '',
                ENABLENOTIFY: 'true',
                ENABLECA: 'true',
                ABEENABLE: 'false',
                ENABLESHOWSNAPSHOT: 'true',
                ENABLESHOWPREVIOUSVERSIONS: 'true',
                smb3EncryptionEnable: 'false',
                unencryptedAccess: 'false',
                OFFLINEFILEMODE: OffLineFileModeE.None,
            },
            /**
             *  new的共享权限列表
             *  默认是有一条所有人的完全控制权限
             */
            permissions: [this.defaultNewPermission],
        },
        new: {
            share: {
                NAME: '',
                SHAREPATH: UNPLACEHOLDER,
                DESCRIPTION: '',
                ENABLENOTIFY: 'true',
                ENABLECA: 'true',
                ABEENABLE: 'false',
                ENABLESHOWSNAPSHOT: 'true',
                ENABLESHOWPREVIOUSVERSIONS: 'true',
                smb3EncryptionEnable: 'false',
                unencryptedAccess: 'false',
                OFFLINEFILEMODE: OffLineFileModeE.None,
            },
            /**
             *  new的共享权限列表
             *  默认是有一条所有人的完全控制权限
             */
            permissions: [this.defaultNewPermission],
        },
        copyFrom: {
            share: {
                NAME: '',
                SHAREPATH: UNPLACEHOLDER,
                DESCRIPTION: '',
                ENABLENOTIFY: 'true',
                ENABLECA: 'true',
                ABEENABLE: 'false',
                ENABLESHOWSNAPSHOT: 'true',
                ENABLESHOWPREVIOUSVERSIONS: 'true',
                smb3EncryptionEnable: 'false',
                unencryptedAccess: 'false',
                OFFLINEFILEMODE: OffLineFileModeE.None,
            },
            permissions: null,
            permissionNumber: 0,
        },
    };
    /**
     * 共享名是否被改变了，如果被用户手动改过，共享名字就不变化了。
     */
    shareNameIsModified = false;
    /**
     * new模式下，权限是否没有变动。
     */
    permissionIsDefault = true;

    /**
     * copyFrom的选中项
     */
    copyFromSelection = null;

    constructor(
        injector: Injector,
        private str: CifsShareStrService,
        private service: CifsShareService,
        private clientCifsShareService: ClientCifsShareService,
        private timeService: TimeService,
        private utilService: UtilService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        if (this.smartGuiCifs) {
            // 更新notify
            this.createShare.new.share.ENABLENOTIFY = this.smartGuiCifs.ENABLENOTIFY;
            this.createShare.new.share.ENABLECA = this.smartGuiCifs.ENABLECA;
            this.createShare.new.share.smb3EncryptionEnable = this.smartGuiCifs.smb3EncryptionEnable;
            this.createShare.new.share.unencryptedAccess = this.smartGuiCifs.unencryptedAccess;
            this.createShare.new.share.ABEENABLE = this.smartGuiCifs.ABEENABLE;
            this.createShare.new.share.ENABLESHOWSNAPSHOT = this.smartGuiCifs.ENABLESHOWSNAPSHOT;
            this.createShare.new.share.ENABLESHOWPREVIOUSVERSIONS = this.smartGuiCifs.ENABLESHOWPREVIOUSVERSIONS;
            this.createShare.new.share.OFFLINEFILEMODE = this.smartGuiCifs.OFFLINEFILEMODE || OffLineFileModeE.None;

            // 如果被推荐了共享名，那就不再变化，除非用户手动修改。
            if (this.smartGuiCifs.NAME) {
                this.shareNameIsModified = true;
                this.createShare.new.share.NAME = this.smartGuiCifs.NAME;
                this.createShare.copyFrom.share.NAME = this.smartGuiCifs.NAME;
            }
        }
        this.switch = this.shareSwitch;
        // 如果被赋值关闭，要手动触发一下changhe事件，避免没有disable form
        if (!this.switch) {
            // 在下一个流程中去禁用，暂无更好方法 待优化
            setTimeout(() => {
                this.onSwitchChange(false);
            });
        }
        this.updateDefaultAccessVal();
        this.initVm();

        if (this.fileSystem?.NAME) {
            // 是否存在cifs共享
            const isExistCifsShare = this.fileSystem?.shareNumbers[1] > 0;

            // 已经创建了cifs共享，cifs共享名称使用时间戳后缀;没有擦护竿见cifs共享时，直接使用文件系统名称
            this.changeSharePathAndName(this.fileSystem?.NAME, isExistCifsShare);
        }
    }

    updateDefaultAccessVal(): void {
        if (this.entry === FileSystemEntryE.AuditLog) {
            this.isAuditLogFs = true;
            // 审计日志文件系统、哈希文件系统和内部文件系统创cifs共享权限默认为只读
            this.defaultNewPermission.PERMISSION = CIFSUserPermissionsE.Read;
            return;
        }

        this.isAuditLogFs = false;
        this.defaultNewPermission.PERMISSION = CIFSUserPermissionsE.All;
    }

    ngOnChanges(changes: SimpleChanges): void {
        // 只有当vstore有值的时候才更新，避免第一次进来就触发
        if (changes.vstoreId && this.vstoreId) {
            this.shareRest = this.service.getBatchRest(this.vstoreId);
            // 还需要初始化一下共享权限的数据，租户切换后，用户属于不同租户的就不生效了。
            this.createShare.copyFrom.permissions = null;
            this.createShare.copyFrom.permissionNumber = 0;
            this.createShare.new.permissions = [this.defaultNewPermission];
            this.permissionIsDefault = true;
        }
        // fsName可能为空，为空也要更新
        if (changes.fsName) {
            this.changeSharePathAndName(this.fsName);
        }
    }

    initVm(): void {
        this.vm.addControl('CIFScreateStyle', new FormControl(this.createStyle, []));
        this.vm.addControl('CIFScopyFromSelect', new FormControl(null, [Validators.required]));
        this.vm.addControl('CIFScreateShare', new FormControl(this.createShare, []));

        this.errorMessage = {
            CIFScopyFromSelect: {
                required: this.i18n.error.copyFromRequired,
            },
        };

        if (this.createStyle === CreateStyleE.New) {
            this.vm.get('CIFScopyFromSelect').disable();
        }
    }

    // 共享开关被切换的时候，需要disable一下
    onSwitchChange($event): void {
        if ($event) {
            this.vm.get('CIFScreateShare').enable();
            this.vm.get('CIFScreateStyle').enable();
            if (this.createStyle === CreateStyleE.CopyFrom) {
                this.vm.get('CIFScopyFromSelect').enable();
            }
            // 下一个周期去渲染下拉
            setTimeout(() => {
                this.shareSelect?.setFixedOption(this.selectShareOpt);
            });
        } else {
            this.vm.get('CIFScreateShare').disable();
            this.vm.get('CIFScreateStyle').disable();
            if (this.createStyle === CreateStyleE.CopyFrom) {
                this.vm.get('CIFScopyFromSelect').disable();
            }
        }
    }

    // copyfrom 的共享被切换的时候
    onCopyShareChange($event): void {
        // 先更新所选中的文件系统
        this.selectShareOpt = $event.option;
        // 无选中项时
        if (!this.selectShareOpt) {
            // 去掉切换时的红色提士 如果不清，每次切换租户都变红，很丑
            // 用异步规避时序问题导致红色错误清除不掉
            Promise.resolve().then(() => {
                (this.vm.get('CIFScopyFromSelect') as FormControl).clearValidate();
            });
        } else {
            // 有选中项时
            // 1、查询并更新这个共享下的client数量
            this.updatePermissions(this.selectShareOpt.data);
            // 2、更新copy的share的其他字段
            this.createShare.copyFrom.share.DESCRIPTION = this.selectShareOpt.data.DESCRIPTION;
            this.createShare.copyFrom.share.ENABLENOTIFY = this.selectShareOpt.data.ENABLENOTIFY;
            this.createShare.copyFrom.share.ENABLECA = this.selectShareOpt.data.ENABLECA;
            this.createShare.copyFrom.share.smb3EncryptionEnable = this.selectShareOpt.data.smb3EncryptionEnable;
            this.createShare.copyFrom.share.unencryptedAccess = this.selectShareOpt.data.unencryptedAccess;
            this.createShare.copyFrom.share.ABEENABLE = this.selectShareOpt.data.ABEENABLE;
            this.createShare.copyFrom.share.ENABLESHOWSNAPSHOT = this.selectShareOpt.data.ENABLESHOWSNAPSHOT;
            this.createShare.copyFrom.share.ENABLESHOWPREVIOUSVERSIONS = this.selectShareOpt.data.ENABLESHOWPREVIOUSVERSIONS;
            this.createShare.copyFrom.share.OFFLINEFILEMODE = this.selectShareOpt.data.OFFLINEFILEMODE;
            // 3、更新copy的share的ID和vstoreId，是为了查询copyFrom的数据
            this.createShare.copyFrom.share.ID = this.selectShareOpt.data.ID;
            this.createShare.copyFrom.share.vstoreId = this.selectShareOpt.data.vstoreId;
            // 4、初始化createShare下的copyfrom中的permissions数据
            this.createShare.copyFrom.permissions = null;
        }
    }

    // 获取共享权限数量，以及全量的client列表
    updatePermissions(cifs: CIFSShare): void {
        if (this.$getCount) {
            this.$getCount.unsubscribe();
            this.$getCount = null;
        }
        this.$getCount = this.clientCifsShareService.getCount(cifs.ID, cifs.vstoreId).pipe(finalize(() => {
            this.$getCount = null;
        })).subscribe(({ data }) => {
            this.createShare.copyFrom.permissionNumber = data.COUNT;
        });
    }

    // 创建模式radio被切换时
    onCreateStyleChange(value: CreateStyleE): void {
        this.createStyle = value;
        const copyFromSelectCtl = this.vm.get('CIFScopyFromSelect');
        if (value === CreateStyleE.All || value === CreateStyleE.New) {
            copyFromSelectCtl.disable();
        } else if (value === CreateStyleE.CopyFrom) {
            copyFromSelectCtl.enable();
            // 去掉切换时的红色提
            (copyFromSelectCtl as FormControl).clearValidate();
        }
    }

    // 获取共享权限数量的资料
    getPermissionsNumStr(): string {
        const style = this.vm.value.CIFScreateStyle;

        if (style === CreateStyleE.All || style === CreateStyleE.New) {
            // 当是new 且 权限还是默认值的时候，返回需要带eveyone
            if (this.permissionIsDefault) {
                return this.i18n.tip.permissionsWithEveryone(
                    this.createShare[style].permissions ?
                        this.createShare[style].permissions.length : this.createShare[style].permissionNumber,
                    this.isAuditLogFs ?
                        this.i18n.tip.everyoneRead :
                        this.i18n.tip.everyone,
                );
            } else {
                return this.i18n.tip.permisssions(
                    this.createShare[style].permissions ?
                        this.createShare[style].permissions.length : this.createShare[style].permissionNumber,
                );
            }
        } else if (style === CreateStyleE.CopyFrom) {
            return this.i18n.tip.permisssions(
                this.createShare[style].permissions ?
                    this.createShare[style].permissions.length : this.createShare[style].permissionNumber,
            );
        } else {
            //
        }
        return '';
    }

    // 获取共享名称，用文件系统名+切割
    getShareName(fsName: string, needAddTimestamp = true): string {
        if (!fsName) {
            return '';
        }

        // 文件系统名称小于等于80长度，不需要拼接时间戳后缀，直接使用文件系统名称做共享名称
        if (!needAddTimestamp && fsName.length <= this.str.cifsShareNameMaxLen) {
            return this.utilService.byteSubstring(fsName, this.str.cifsShareNameMaxLen);
        }
        const tempStr = '_';
        const timeLength = this.timestamp.length;
        // 80 是指CIFS共享名称的长度。
        return this.utilService.byteSubstring(fsName,
            this.str.cifsShareNameMaxLen - timeLength - tempStr.length) + tempStr + this.timestamp;
    }

    // 文件系统名改变的时候，自动组装path和共享名称
    changeSharePathAndName(fsName: string, needAddTimestamp = true): void {
        if (!fsName) {
            return;
        }
        const path = fsName ? '/' + fsName + '/' : UNPLACEHOLDER;
        const name = this.getShareName(fsName, needAddTimestamp);
        this.createShare.all.share.SHAREPATH = path;
        this.createShare.new.share.SHAREPATH = path;
        this.createShare.copyFrom.share.SHAREPATH = path;
        // 只有名字没有被用户手动改过才赋值
        if (!this.shareNameIsModified) {
            this.sendShareNameChangedData.emit(name);
            this.createShare.all.share.NAME = name;
            this.createShare.new.share.NAME = name;
            this.createShare.copyFrom.share.NAME = name;
        }
    }

    modifyShare(): void {
        const style: CreateStyleE.CopyFrom | CreateStyleE.New = this.vm.value.CIFScreateStyle;
        // 复用的 cifs的创建页面，并且要传入新的title

        this.doAction(CreateCifsShareComponent, {
            copyFromStyle: style,
            copyFromShare: this.createShare[style].share,
            copyFromPermissions: this.createShare[style].permissions,
            entry: this.entry,
            title: this.i18n.title.modifyShare,
            vstoreName: this.vstoreName,
            vstoreId: this.vstoreId,
            supportSmartGui: this.supportSmartGui,
            // 传入 -- 是为了展示用
            fileSystem: { NAME: this.fsName ? this.fsName : UNPLACEHOLDER },
            onClose: ({ data }: { data: { share: CIFSShare; permissions: CIFSClient[] } }) => {
                // 如果没有dada，说明没有改变值
                if (!data) {
                    return;
                }
                if (data.share.NAME !== this.createShare[style].share.NAME) {
                    this.createShare.new.share.NAME = data.share.NAME;
                    this.createShare.copyFrom.share.NAME = data.share.NAME;
                    this.shareNameIsModified = true;
                }
                this.createShare[style].share = data.share;
                this.createShare[style].permissions = data.permissions;
                // data.permissions在CreateCifsShareComponent中深度拷贝过一次，所以这里判断不用考虑引用问题
                // 当data.permissions和默认的defaultNewPermission不相等或者数量不是1的时候，说明被更改过了，就不展示everyone
                if (style === CreateStyleE.New) {
                    if (data.permissions.length === 1 && isEqual(this.defaultNewPermission, data.permissions[0])) {
                        this.permissionIsDefault = true;
                    } else {
                        this.permissionIsDefault = false;
                    }
                }
            },
        });
    }
}
